<div class="bv-legal">
  <header class="bv-legal__topbar">
    <a routerLink="/" class="bv-legal__logo-link" aria-label="BracketsVault home">
      <img src="images/logo.png" alt="BracketsVault" class="bv-legal__logo" />
    </a>
    <div class="bv-legal__topbar-actions">
      <a bitButton buttonType="secondary" routerLink="/login">Log in</a>
      <a bitButton buttonType="primary" routerLink="/signup">Create account</a>
    </div>
  </header>

  <section class="bv-legal__hero" aria-labelledby="privacy-hero-title">
    <h1 id="privacy-hero-title" class="bv-legal__hero-title">Brackets Vault Privacy Policy</h1>
    <p class="bv-legal__hero-meta">Last revised: August 2026</p>
    <p class="bv-legal__hero-lead">
      How Brackets Ltd. collects, uses, and protects your information when you use Brackets Vault.
    </p>
  </section>

  <div class="bv-legal__main">
    <nav class="bv-legal__toc" aria-label="Table of contents">
      <p class="bv-legal__toc-title">On this page</p>
      <ol class="bv-legal__toc-list">
        @for (section of tocSections; track section.id) {
          <li>
            <button type="button" class="bv-legal__toc-link" (click)="scrollToSection(section.id)">
              {{ section.label }}
            </button>
          </li>
        }
      </ol>
    </nav>

    <div class="bv-legal__content">
      <section class="bv-legal-section bv-legal-section--intro">
        <p>
          Brackets Ltd. (&ldquo;Brackets&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, or
          &ldquo;us&rdquo;) values your privacy and is committed to protecting your personal
          information. This Privacy Policy explains how we collect, use, store, process, and protect
          your information when you use Brackets Vault, our password and secrets management
          solution, the Brackets website, and related services.
        </p>
        <p>
          By using Brackets Vault, you agree to the collection and use of information in accordance
          with this Privacy Policy.
        </p>
        <p>
          <strong>Application URL:</strong>
          <a
            bitLink
            href="https://app.vault.bracketsltd.com/"
            target="_blank"
            rel="noopener noreferrer"
            >https://app.vault.bracketsltd.com/</a
          >
        </p>
      </section>

      <section id="legal-section-1" class="bv-legal-section" aria-labelledby="privacy-section-1">
        <h2 id="privacy-section-1">1. Information We Collect</h2>
        <p>Brackets Vault processes two categories of information:</p>
        <ul>
          <li>Vault Data</li>
          <li>Administrative Data</li>
        </ul>

        <h3>1.1 Vault Data</h3>
        <p>
          Vault Data includes all information that you choose to store within Brackets Vault,
          including but not limited to login credentials, passwords and passkeys, secure notes, API
          keys and secrets, attachments, images, documents, and any other information added to your
          vault.
        </p>
        <p>
          <strong>Zero-knowledge by design.</strong> Vault Data is encrypted on your device before
          it reaches our servers. Brackets cannot access, read, or decrypt your Vault Data. You may
          add, modify, or delete it at any time.
        </p>

        <h3>1.2 Administrative Data</h3>
        <p>
          We collect information necessary to provide and maintain Brackets Vault, including name,
          email address, phone number, company name, billing information, account preferences,
          subscription details, login activity, number of items stored in your vault, and technical
          or support communications.
        </p>
        <p>
          We use this information to create and manage your account, provide support, process
          payments, verify subscriptions, send security notifications, improve performance, prevent
          abuse, and comply with legal obligations. Administrative Data is retained while your
          account is active and for any additional period required by law.
        </p>
      </section>

      <section id="legal-section-2" class="bv-legal-section" aria-labelledby="privacy-section-2">
        <h2 id="privacy-section-2">2. Website and Application Information</h2>
        <p>
          When you visit our website or use Brackets Vault, we may collect name, business name,
          email address, telephone number, IP address, device identifiers, browser and operating
          system information, network information, usage statistics, and information submitted
          through forms and support requests.
        </p>
        <p>
          We use this information to provide services, respond to inquiries, improve our products,
          send important notices, secure our systems, diagnose technical issues, analyze usage
          patterns, and comply with legal obligations.
        </p>
      </section>

      <section id="legal-section-3" class="bv-legal-section" aria-labelledby="privacy-section-3">
        <h2 id="privacy-section-3">3. Analytics and Usage Data</h2>
        <p>
          We collect analytics and usage information to understand how Brackets Vault is used and to
          improve user experience. This may include pages visited, session duration, device type,
          browser type, operating system, performance metrics, and error reports.
        </p>
        <p>
          This information is used solely to improve the performance, reliability, and functionality
          of Brackets Vault.
        </p>
      </section>

      <section id="legal-section-4" class="bv-legal-section" aria-labelledby="privacy-section-4">
        <h2 id="privacy-section-4">4. How We Use Your Information</h2>
        <p>We may use your information to:</p>
        <ul>
          <li>Operate Brackets Vault and authenticate users</li>
          <li>Provide account management and customer support</li>
          <li>Process payments and send service notifications</li>
          <li>Improve products, infrastructure, and security</li>
          <li>Detect fraud and abuse and fulfill legal obligations</li>
        </ul>
        <p>
          We will only use your information for the purposes described in this Privacy Policy or as
          otherwise required by law.
        </p>
      </section>

      <section id="legal-section-5" class="bv-legal-section" aria-labelledby="privacy-section-5">
        <h2 id="privacy-section-5">5. Data Retention</h2>
        <p>
          We retain information only for as long as necessary to provide services, maintain your
          account, meet legal requirements, resolve disputes, enforce agreements, and protect our
          systems.
        </p>
        <p>
          Upon account termination, we will delete or anonymize your personal information in
          accordance with our data retention policies unless we are legally required to retain it.
        </p>
      </section>

      <section id="legal-section-6" class="bv-legal-section" aria-labelledby="privacy-section-6">
        <h2 id="privacy-section-6">6. Access, Correction, and Deletion</h2>
        <p>
          You may request to access, correct, update, or delete your personal information, or delete
          your account entirely. Contact us using the details below — we may verify your identity
          before processing your request.
        </p>
      </section>

      <section id="legal-section-7" class="bv-legal-section" aria-labelledby="privacy-section-7">
        <h2 id="privacy-section-7">7. Information Sharing</h2>
        <p><strong>Brackets does not sell your personal information.</strong></p>
        <p>
          We may share information only with infrastructure providers, payment processors, support
          providers, or when required by law, to protect security, prevent fraud, or in connection
          with a merger or acquisition. Third parties must contractually protect your information.
        </p>
      </section>

      <section id="legal-section-8" class="bv-legal-section" aria-labelledby="privacy-section-8">
        <h2 id="privacy-section-8">8. International Data Transfers</h2>
        <p>
          Your information may be processed and stored in countries other than your country of
          residence. We take reasonable measures to ensure personal information remains protected in
          accordance with applicable data protection laws.
        </p>
      </section>

      <section id="legal-section-9" class="bv-legal-section" aria-labelledby="privacy-section-9">
        <h2 id="privacy-section-9">9. Security</h2>
        <p>
          Brackets Vault uses industry-standard security practices including end-to-end encryption,
          AES-256 encryption, TLS/SSL, access controls, authentication systems, and continuous
          monitoring. Vault Data is encrypted before leaving your device.
        </p>
        <p>
          Although we strive to protect your information, no method of transmission or storage can
          be guaranteed to be completely secure.
        </p>
      </section>

      <section id="legal-section-10" class="bv-legal-section" aria-labelledby="privacy-section-10">
        <h2 id="privacy-section-10">10. Cookies</h2>
        <p>
          Brackets Vault and our website use cookies to keep users signed in, remember preferences,
          improve performance, enhance security, and analyze usage.
        </p>
        <h3>Strictly Necessary Cookies</h3>
        <p>Required for authentication, security, and essential functionality.</p>
        <h3>Functional Cookies</h3>
        <p>
          Used to understand usage patterns and improve user experience. You can manage preferences
          through your browser settings.
        </p>
      </section>

      <section id="legal-section-11" class="bv-legal-section" aria-labelledby="privacy-section-11">
        <h2 id="privacy-section-11">11. Third-Party Links</h2>
        <p>
          Brackets Vault may contain links to third-party websites. We are not responsible for their
          privacy practices — please review their policies before sharing personal information.
        </p>
      </section>

      <section id="legal-section-12" class="bv-legal-section" aria-labelledby="privacy-section-12">
        <h2 id="privacy-section-12">12. Children&rsquo;s Privacy</h2>
        <p>
          Brackets Vault is not intended for individuals under 13, or the minimum age required in
          the relevant jurisdiction. We do not knowingly collect personal information from children.
          If we become aware of such collection, we will take reasonable steps to delete it.
        </p>
      </section>

      <section id="legal-section-13" class="bv-legal-section" aria-labelledby="privacy-section-13">
        <h2 id="privacy-section-13">13. Updates to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Significant changes may be
          communicated via email, in-app notices, or on our website. The latest version is always
          available at
          <a bitLink routerLink="/privacy">https://app.vault.bracketsltd.com/#/privacy</a>.
        </p>
      </section>

      <section id="legal-section-14" class="bv-legal-section" aria-labelledby="privacy-section-14">
        <h2 id="privacy-section-14">14. Contact Us</h2>
        <p>
          If you have questions, concerns, or requests related to this Privacy Policy, please
          contact us:
        </p>
        <ul class="bv-legal__contact-list">
          <li>
            <strong>Website:</strong>
            <a bitLink href="https://www.bracketsltd.com/" target="_blank" rel="noopener noreferrer"
              >bracketsltd.com</a
            >
          </li>
          <li>
            <strong>Application:</strong>
            <a
              bitLink
              href="https://app.vault.bracketsltd.com/"
              target="_blank"
              rel="noopener noreferrer"
              >app.vault.bracketsltd.com</a
            >
          </li>
          <li>
            <strong>Contact page:</strong>
            <a
              bitLink
              href="https://www.bracketsltd.com/contact"
              target="_blank"
              rel="noopener noreferrer"
              >bracketsltd.com/contact</a
            >
          </li>
          <li>
            <strong>Email:</strong>
            <a bitLink href="mailto:support@bracketsltd.com">support@bracketsltd.com</a>
          </li>
        </ul>
      </section>
    </div>
  </div>

  <footer class="bv-legal__footer">
    <p class="bv-legal__footer-copy">&copy; {{ year }} Brackets Ltd.</p>
    <nav class="bv-legal__footer-links" aria-label="Footer">
      <a bitLink routerLink="/login">Log in</a>
      <span class="bv-legal__footer-sep" aria-hidden="true">·</span>
      <a bitLink routerLink="/signup">Create account</a>
    </nav>
  </footer>
</div>
